import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { motion } from "framer-motion";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import ParallaxImage from "../components/ui/ParallaxImage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  User,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Link2,
  BookOpen,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../apiConfig";

const estimateReadTime = (text) => {
  if (!text) return 1;
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 220));
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const [artRes, allRes] = await Promise.all([
          axios.get(`${API}/articles/${slug}`),
          axios.get(`${API}/articles?limit=10`),
        ]);
        setArticle(artRes.data);

        // Increment view count
        axios.post(`${API}/articles/${slug}/view`).catch(() => {});

        // Pick related articles (same category, excluding current)
        const all = Array.isArray(allRes.data) ? allRes.data : [];
        const others = all.filter((a) => a.slug !== slug);
        const sameCategory = others.filter(
          (a) => a.category === artRes.data.category
        );
        setRelated(
          sameCategory.length >= 2 ? sameCategory.slice(0, 2) : others.slice(0, 2)
        );
      } catch (err) {
        console.error("Failed to load article:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-36 max-w-3xl mx-auto px-6 animate-pulse space-y-6">
          <div className="h-6 w-32 bg-muted rounded" />
          <div className="h-12 w-3/4 bg-muted rounded" />
          <div className="h-[50vh] bg-muted rounded-xl" />
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-4 bg-muted rounded w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-36 text-center px-6">
          <h1 className="text-3xl font-light mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/the-collection">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to the Collection
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const readTime = estimateReadTime(article.content);

  return (
    <div className="min-h-screen bg-background" data-testid="article-page">
      <Navigation />

      {/* Hero */}
      <article className="pt-32 pb-24">
        {/* Back link + Meta */}
        <div className="max-w-3xl mx-auto px-6 md:px-8 mb-10">
          <Link
            to="/the-collection"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
            data-testid="article-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            Back to the Collection
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Category + Read time */}
            <div className="flex items-center gap-4 mb-5">
              {article.category && (
                <Badge
                  variant="outline"
                  className="capitalize rounded-full px-3 text-xs tracking-wide"
                  data-testid="article-category"
                >
                  {article.category.replace("-", " ")}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                {readTime} min read
              </span>
            </div>

            {/* Title */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] mb-6"
              data-testid="article-title"
            >
              <em>{article.title}</em>
            </h1>

            {/* Excerpt */}
            {article.excerpt && (
              <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-2xl">
                {article.excerpt}
              </p>
            )}

            {/* Author + Date */}
            <div className="flex items-center gap-5 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                {article.author || "Art By Arawol"}
              </span>
              {article.created_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(article.created_at).toLocaleDateString("en-AU", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              )}
              {article.views > 0 && (
                <span className="flex items-center gap-1.5">
                  <BookOpen className="h-3.5 w-3.5" />
                  {article.views} views
                </span>
              )}
            </div>
          </motion.div>
        </div>

        {/* Featured Image — full-bleed spread */}
        {article.featured_image && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-16"
          >
            <ParallaxImage
              src={article.featured_image}
              alt={article.title}
              className="h-[55vh] md:h-[75vh]"
              eager
              data-testid="article-featured-image"
            />
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-3xl mx-auto px-6 md:px-8"
        >
          <div
            className="article-body prose prose-lg prose-neutral dark:prose-invert max-w-none
              prose-headings:font-light prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2
              prose-p:leading-relaxed prose-p:text-muted-foreground
              prose-strong:text-foreground prose-strong:font-medium
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-blockquote:border-l-primary prose-blockquote:italic prose-blockquote:text-muted-foreground
              prose-li:text-muted-foreground
              prose-hr:border-border"
            data-testid="article-content"
          >
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>

          {/* Tags */}
          {article.tags?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {article.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="rounded-full text-xs"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 pt-8 border-t border-border">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Share2 className="h-4 w-4" />
                Share
              </span>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                data-testid="share-twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                data-testid="share-facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                data-testid="share-linkedin"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <button
                onClick={copyLink}
                className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                data-testid="share-copy-link"
              >
                <Link2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="py-20 px-6 md:px-8 bg-muted/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <p className="text-[11px] tracking-[0.3em] uppercase text-primary mb-3">
              Continue Reading
            </p>
            <h2 className="text-2xl md:text-3xl font-light mb-10">
              <em>Related Stories</em>
            </h2>
            <div className="grid md:grid-cols-2 gap-10">
              {related.map((rel, i) => (
                <Link
                  key={rel.slug || i}
                  to={`/articles/${rel.slug}`}
                  className="group block"
                  data-testid={`related-article-${i}`}
                >
                  {rel.featured_image && (
                    <div className="aspect-[16/9] overflow-hidden rounded-xl mb-4">
                      <img
                        src={rel.featured_image}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-2">
                    {rel.category && (
                      <span className="text-[10px] tracking-[0.2em] uppercase text-primary font-medium">
                        {rel.category.replace("-", " ")}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-light group-hover:text-primary transition-colors leading-snug">
                    <em>{rel.title}</em>
                  </h3>
                  {rel.excerpt && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {rel.excerpt}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
          Explore the Collection
        </p>
        <h2 className="text-2xl md:text-3xl font-light mb-6">
          <em>Discover More</em>
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/the-collection">
            <Button className="rounded-full px-8 h-12" data-testid="article-cta-collection">
              Browse the Collection
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
          <Link to="/commissions">
            <Button variant="outline" className="rounded-full px-8 h-12">
              Our Services
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
